import dayjs, { Dayjs } from 'dayjs'
import { Duration } from 'dayjs/plugin/duration'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AccountRecord, useContentSearchCountLazyQuery } from '../../../api/client'
import AccountFilter from '../../../components/content/search/AccountFilter'
import DateTimePickerContainer from '../../../components/content/search/DateTimePickerContainer'
import TeamFilter from '../../../components/content/search/TeamFilter'
import SearchParameters, {
    mapToContentSearchParams
} from '../../../components/content/search/searchParameters'
import { formatCappedCount } from '../../../util/searchCounts'

type SearchDefinitionStepProps = {
    searchParameters: SearchParameters
    onSearchParameterChange: (params: SearchParameters) => void
    showTeamSearch: boolean
    onNext: () => void
}

export default function SearchDefinitionStep({
    searchParameters,
    onSearchParameterChange,
    showTeamSearch,
    onNext
}: SearchDefinitionStepProps) {
    const [previewCount, setPreviewCount] = useState<number | null>(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const [fetchCount] = useContentSearchCountLazyQuery({
        fetchPolicy: 'no-cache'
    })

    const handleAccountsChange = (accounts: AccountRecord[]) => {
        onSearchParameterChange({
            ...searchParameters,
            selectedAccountIds: accounts.map((account) => account.id)
        })
    }

    const handleTeamsChange = (teamIds: number[]) => {
        onSearchParameterChange({
            ...searchParameters,
            selectedTeams: teamIds
        })
    }

    const handleDateChange = (start?: Dayjs, end?: Dayjs, duration?: Duration) => {
        onSearchParameterChange({
            ...searchParameters,
            startDateTime: start,
            endDateTime: end,
            timeDuration: duration
        })
    }

    const hasRequiredFields =
        searchParameters.selectedAccountIds.length > 0 &&
        (searchParameters.timeDuration ||
            (searchParameters.startDateTime && searchParameters.endDateTime))

    const fetchPreviewCount = useCallback(async () => {
        const params = mapToContentSearchParams(searchParameters)
        setPreviewLoading(true)
        try {
            const result = await fetchCount({ variables: { params } })
            setPreviewCount(result.data?.contentSearchCount ?? null)
        } catch {
            // Don't block the user on count errors — just hide the preview
            setPreviewCount(null)
        } finally {
            setPreviewLoading(false)
        }
    }, [fetchCount, searchParameters])

    useEffect(() => {
        if (!hasRequiredFields) {
            setPreviewCount(null)
            setPreviewLoading(false)
            return
        }

        setPreviewLoading(true)
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }
        debounceRef.current = setTimeout(() => {
            fetchPreviewCount()
        }, 500)

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
        }
    }, [hasRequiredFields, fetchPreviewCount])

    // Resolve the date range for display
    const resolvedStart = searchParameters.startDateTime
        ? searchParameters.startDateTime
        : searchParameters.timeDuration
          ? dayjs().subtract(searchParameters.timeDuration)
          : null
    const resolvedEnd = searchParameters.endDateTime ? searchParameters.endDateTime : dayjs()

    return (
        <div className="mx-auto max-w-3xl">
            <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-200 p-8">
                <h2 className="font-sans text-xl font-medium text-ink-900 mb-2">
                    Define your search
                </h2>
                <p className="text-ink-600 mb-8">
                    Select the profiles and filters to define the dataset Ask Arwen will analyse.
                </p>

                <div className="space-y-6">
                    {/* Teams - Optional */}
                    {showTeamSearch && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Teams
                            </label>
                            <TeamFilter
                                teamIds={searchParameters.selectedTeams}
                                onUpdateSelectedTeams={handleTeamsChange}
                            />
                        </div>
                    )}

                    {/* Profiles - Required */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Profiles <span className="text-red-500">*</span>
                        </label>
                        <AccountFilter
                            selectedAccountIds={searchParameters.selectedAccountIds}
                            selectedTeamIds={searchParameters.selectedTeams}
                            onUpdateSelectedAccounts={handleAccountsChange}
                        />
                        {searchParameters.selectedAccountIds.length === 0 && (
                            <p className="mt-1 text-sm text-amber-600">
                                Please select at least one profile
                            </p>
                        )}
                    </div>

                    {/* Date Range - Required */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Date range <span className="text-red-500">*</span>
                        </label>
                        <DateTimePickerContainer
                            startDateTime={searchParameters.startDateTime}
                            endDateTime={searchParameters.endDateTime}
                            timeDuration={searchParameters.timeDuration}
                            onDateChange={handleDateChange}
                            useDefault={true}
                        />
                    </div>
                </div>

                {/* Dataset Preview */}
                {hasRequiredFields && (
                    <div className="mt-6">
                        {previewLoading && (
                            <div className="flex items-center gap-2 rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-500 ring-1 ring-gray-200">
                                <svg
                                    className="h-4 w-4 animate-spin text-gray-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    />
                                </svg>
                                Counting matching items...
                            </div>
                        )}
                        {!previewLoading && previewCount !== null && previewCount === 0 && (
                            <div className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-700 ring-1 ring-amber-200">
                                No items found for the selected filters and date range. Try
                                adjusting your search criteria.
                            </div>
                        )}
                        {!previewLoading && previewCount !== null && previewCount > 0 && (
                            <div className="rounded-md bg-ink-50 px-4 py-3 text-sm text-ink-700 ring-1 ring-ink-200">
                                ~{formatCappedCount(previewCount)} items
                                {resolvedStart && resolvedEnd && (
                                    <span className="text-ink-500">
                                        {' '}
                                        from {resolvedStart.format('MMM D, YYYY')} &mdash;{' '}
                                        {resolvedEnd.format('MMM D, YYYY')}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Navigation */}
                <div className="mt-10 flex justify-end">
                    <button
                        type="button"
                        onClick={onNext}
                        disabled={!hasRequiredFields}
                        className="inline-flex items-center rounded-md bg-arwen-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-arwen-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-arwen-600 disabled:cursor-not-allowed disabled:bg-ink-200 disabled:text-ink-400"
                    >
                        Next: review dataset
                        <svg
                            className="ml-2 h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    )
}
